import React, { useEffect, useState } from 'react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Center,
  Divider,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  Text,
  useColorMode,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { unwrapResult } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { toZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SEHomeHeaderIcon } from 'clipsal-cortex-icons/src/custom-icons';
import { ChargerStatus } from 'clipsal-cortex-types/src/api/api-evcharger';
import { getTimeBetweenDates } from 'clipsal-cortex-utils/src/calculations/date-utils';
import { useInterval } from 'clipsal-cortex-utils/src/hooks/use-interval';

import { useReduxDispatch } from '../../../app/store';
import chargingGifImgDark from '../../../assets/images/dark_animation.gif';
import chargingGifImgLight from '../../../assets/images/light_animation.gif';
import { DataSummaryCard } from '../../../common/components/DataSummaryCard';
import { SEHomeCard } from '../../../common/components/SEHomeCard';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { selectEvChargers, selectSite } from '../../site/siteSlice';
import { CHARGER_STATUS_UI_CONFIG, formatTotalTime } from '../ev-charger-helper';
import { useCheckIfCurrentlyInSchedule } from '../schedules/scheduler-helper';
import { fetchEvChargerSchedules, selectEvChargerSchedules } from '../schedules/schedulerSlice';
import { ChargingControl } from './ChargingControl';
import { fetchEvChargerStatus, selectEvChargerData, setIsLoaded } from './evChargerSlice';

const STATUS_POLL_INTERVAL = 5000; // milliseconds

// These statuses will not have description below charging status.
const NO_DESCRIPTION_STATUSES: ChargerStatus[] = ['Preparing'];

/* istanbul ignore next -- @preserve */
export const DeviceStatus = () => {
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const site = useSelector(selectSite);
  const evChargers = useSelector(selectEvChargers);
  const {
    siteDeviceId,
    isLoaded,
    isUpdating,
    status,
    power,
    energy_consumed: energyConsumed,
    session_time: sessionTime,
    error_title: errorTitle,
    error_message: errorMessage,
  } = useSelector(selectEvChargerData);
  const { colorMode } = useColorMode();

  const chargingAnimationImg = useColorModeValue(chargingGifImgLight, chargingGifImgDark);
  const chargerImg = CHARGER_STATUS_UI_CONFIG[status as ChargerStatus]?.imgSrc;
  const totalTime = formatTotalTime(sessionTime);
  const hasEvChargers = !!evChargers?.length;
  const canPollStatus = hasEvChargers && isLoaded;
  const iconColor = useColorModeValue('customGrey.900', 'customGrey.400');

  const { isLoaded: areSchedulesLoaded, schedules } = useSelector(selectEvChargerSchedules);
  const isTimedScheduleEnabled = schedules.some((schedule) => !schedule.isEditing);
  const { isInSchedule, endTime } = useCheckIfCurrentlyInSchedule(schedules, site.timezone);
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null);
  const isWaitingForSchedule = isTimedScheduleEnabled && status === 'SuspendedEVSE';
  const isFaulty = status === 'Faulted';
  const chargerStateTitle = () => {
    if (isFaulty) {
      return errorTitle;
    }

    return isWaitingForSchedule ? t('EVSE.charging scheduled') : t(`EVSE.statusKey.${status}`);
  };
  const chargerStateDescription = () => {
    if (isFaulty) {
      return errorMessage;
    }

    return isWaitingForSchedule ? t('EVSE.charging scheduled text') : t(`EVSE.statusDescKey.${status}`);
  };

  // Polling will start after first status update.
  useInterval(
    async () => {
      await fetchEvChargerStatusData();
    },
    canPollStatus ? STATUS_POLL_INTERVAL : null
  );

  useInterval(() => {
    if (isTimedScheduleEnabled) {
      if (isInSchedule && status === 'Charging') {
        // Get the current time in the site's timezone.
        const currentSiteTime = toZonedTime(new Date(), site.timezone);
        const { hours, minutes, seconds } = getTimeBetweenDates(currentSiteTime, endTime!);

        setTimeRemaining(
          `${hours}:${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`
        );
      } else {
        setTimeRemaining(null);
      }
    }
  }, 400);

  const fetchEvChargerStatusData = async () => {
    const evChargerId = evChargers[0].id;
    if (!evChargerId || isUpdating) return;

    try {
      unwrapResult(await dispatch(fetchEvChargerStatus(evChargerId.toString())));
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'DeviceStatus',
          method: 'fetchEvChargerStatus',
        },
      });
      toast({ title: t('EVSE.could not get charger status'), status: 'error', isClosable: true });
    }
  };

  useEffect(() => {
    if (isLoaded) return;

    // If site has EV chargers, fetch status data else navigate to the No Device screen.
    if (hasEvChargers) {
      fetchEvChargerStatusData();
    } else {
      dispatch(setIsLoaded(true));
      navigate('../device-not-found', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, hasEvChargers, navigate, dispatch]);

  // Fetch schedules if not fetched yet.
  useEffect(() => {
    if (siteDeviceId && !areSchedulesLoaded) {
      dispatch(fetchEvChargerSchedules(siteDeviceId));
    }
  }, [areSchedulesLoaded, dispatch, siteDeviceId]);

  return (
    <SlidingAnimationPageBase
      includeTopNavHamButton
      includeSchneiderHamButton
      includeBottomNav
      title={<SEHomeHeaderIcon w={'100%'} color={iconColor} />}
    >
      <Center flexDirection="column" px={3} textAlign="center">
        <SkeletonCircle mt={2} mx="auto" w={240} h={240} isLoaded={isLoaded}>
          <Box data-testid="ev-image-container" position={'relative'}>
            {/* Gif image rendered behind the circle. */}
            {status === 'Charging' && <Image src={chargingAnimationImg} alt="Charging" maxW={234} />}
            <Image
              data-testid="ev-status-image"
              src={chargerImg?.[colorMode]}
              alt="Charging Station Status"
              {...(status === 'Charging' ? { position: 'absolute', right: 0, bottom: 0 } : { mb: 1, mx: 'auto' })}
            />
          </Box>
        </SkeletonCircle>

        <SkeletonText mx={'auto'} mt={2} isLoaded={isLoaded} spacing={5}>
          <Heading data-testid="charging-status-title">{chargerStateTitle() || t('EVSE.unavailable')}</Heading>

          {!NO_DESCRIPTION_STATUSES.includes(status) && (
            <Text data-testid="charging-status-description" mt={1}>
              {chargerStateDescription() ||
                t('Common.something went wrong') + ' ' + t('Common.if this persists contact support')}
            </Text>
          )}
        </SkeletonText>

        <SimpleGrid gap={3} columns={2} mt={4} w="100%">
          <DataSummaryCard
            title={timeRemaining ? t('EVSE.remaining time') : t('EVSE.session time')}
            isLoaded={!!isLoaded}
            value={timeRemaining || totalTime || '0:00:00'}
            data-testid="session-time-card"
            w="100%"
            h="100%"
          />
          <DataSummaryCard
            title={status === 'Charging' ? t('Common.charging power') : t('EVSE.total energy')}
            isLoaded={!!isLoaded}
            value={Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 2 }).format(
              Math.abs(status === 'Charging' ? power : energyConsumed) ?? 0
            )}
            suffix={status === 'Charging' ? 'kW' : 'kWh'}
            data-testid="current-power-card"
            w="100%"
            h="100%"
          />
        </SimpleGrid>

        <Skeleton isLoaded={true} borderRadius={5} borderBottomRightRadius={'25px'} mt={3} w="100%">
          <SEHomeCard borderTopLeftRadius={5} borderBottomRightRadius={'25px'} textAlign={'center'}>
            <Flex
              w="100%"
              py={2}
              as="button"
              onClick={() => navigate('../device-charging-activity')}
              data-testid="device-charging-activity-button"
            >
              <Text>{t('EVSE.charging activity')}</Text>
              <ChevronRightIcon color={'customGrey.400'} ml="auto" w={6} h={6} />
            </Flex>

            <Divider borderColor={'rgba(151, 151, 151, 0.3)'} my={4} />
            <Flex
              w="100%"
              py={2}
              as="button"
              onClick={() => navigate('../device-charging-schedule')}
              data-testid="device-charging-schedule-button"
            >
              <Text>{t('EVSE.charging schedule')}</Text>
              <ChevronRightIcon color={'customGrey.400'} ml="auto" w={6} h={6} />
            </Flex>
          </SEHomeCard>
        </Skeleton>

        <ChargingControl />
      </Center>
    </SlidingAnimationPageBase>
  );
};
